import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Link,
  Stack,
  Typography,
} from '@mui/material';
import SportsRugbyIcon from '@mui/icons-material/SportsRugby';
import SportsSoccerIcon from '@mui/icons-material/SportsSoccer';

import { fetchCompetitionResults } from './ResultsApi';
import { useRef } from 'react';

const getTeamPhoto = (team) => {
  if (team?.logoUrl) return team.logoUrl;
  return '';
};

const buildTeamUrl = (team) => {
  if (!team?._id) return null;
  const teamLabel = team?.name || team?.fullName || team?._id;
  return `/dsas-view/equipe/${encodeURIComponent(teamLabel)}_${team._id}`;
};

const formatDateLabel = (date) => {
  if (!date) return '';
  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return '';

  return new Intl.DateTimeFormat('fr-FR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(parsed);
};

const formatTimeLabel = (value) => {
  if (!value) return '—';

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return String(value);
  }

  return new Intl.DateTimeFormat('fr-FR', {
    hour: '2-digit',
    minute: '2-digit',
  }).format(parsed);
};

const hasValidScore = (match) =>
  typeof match?.homeScore === 'number' &&
  typeof match?.awayScore === 'number';

const getWinnerFlags = (match) => {
  if (!hasValidScore(match)) {
    return { homeWon: false, awayWon: false };
  }

  return {
    homeWon: match.homeScore > match.awayScore,
    awayWon: match.awayScore > match.homeScore,
  };
};

const parsePositiveDay = (value) => {
  if (value == null || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? Math.floor(parsed) : null;
};

const renderCenterContent = (match) => {
  const hasScore = hasValidScore(match);

  if (!hasScore) {
    return (
      <Typography variant="body1" className="results-match__time">
        {formatTimeLabel(match.dateTime || match.time)}
      </Typography>
    );
  }

  return (
    <Box className="results-match__score-pair">
      <Box className="results-match__score-box">{match.homeScore}</Box>
      <Box className="results-match__score-box">{match.awayScore}</Box>
    </Box>
  );
};

const normalizeDayValue = (value) => {
  if (value == null) return null;
  const normalized = String(value).trim();
  return normalized !== '' ? normalized : null;
};

export default function ResultsSection({
  discipline,
  competitionIds = [],
  season,
  day = null,
  groupKey = null,
  widgetConfigurationId = null,
  onNavigate,
  onMetaChange,
}) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState({
    items: [],
    selectedDay: null,
    nbDays: null,
  });

  const competitionKey = useMemo(
    () => competitionIds?.join(',') || '',
    [competitionIds]
  );

  const inFlightRequestKeyRef = useRef(null);

  const requestKey = useMemo(
    () =>
      JSON.stringify({
        discipline: discipline || '',
        competitionKey,
        season: season || '',
        day: normalizeDayValue(day) || '',
        groupKey: groupKey || '',
        widgetConfigurationId: widgetConfigurationId || '',
      }),
    [discipline, competitionKey, season, day, groupKey, widgetConfigurationId]
  );

  useEffect(() => {
    let ignore = false;

    if (!discipline || !competitionKey) {
      setLoading(false);
      setError('');
      setData({
        items: [],
        selectedDay: null,
        nbDays: null,
      });
      return;
    }

    if (inFlightRequestKeyRef.current === requestKey) {
      return;
    }

    const load = async () => {
      try {
        inFlightRequestKeyRef.current = requestKey;
        setLoading(true);
        setError('');

        const result = await fetchCompetitionResults({
          discipline,
          competitionIds,
          season,
          widgetConfigurationId,
          day,
          groupKey,
        });

        if (!ignore) {
          const nextData = {
            items: Array.isArray(result?.items) ? result.items : [],
            selectedDay: normalizeDayValue(result?.selectedDay),
            nbDays: parsePositiveDay(result?.nbDays),
          };

          setData((prev) => {
            const sameItems =
              JSON.stringify(prev.items) === JSON.stringify(nextData.items);

            if (
              sameItems &&
              prev.selectedDay === nextData.selectedDay &&
              prev.nbDays === nextData.nbDays
            ) {
              return prev;
            }

            return nextData;
          });

          onMetaChange?.({
            selectedDay: nextData.selectedDay,
            nbDays: nextData.nbDays,
          });
        }
      } catch (e) {
        if (!ignore) {
          setError('Impossible de charger les résultats.');
          setData({
            items: [],
            selectedDay: null,
            nbDays: null,
          });

          onMetaChange?.({
            selectedDay: null,
            nbDays: null,
          });
        }
      } finally {
        if (!ignore) {
          setLoading(false);
        }
        if (inFlightRequestKeyRef.current === requestKey) {
          inFlightRequestKeyRef.current = null;
        }
      }
    };

    load();

    return () => {
      ignore = true;
    };
  }, [requestKey]);

  const handleLinkClick = (event, url) => {
    if (!url || !onNavigate) return;
    event.preventDefault();
    onNavigate(url);
  };

  if (loading) {
    return (
      <Box className="results-widget__loader">
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error" className="results-widget__alert">{error}</Alert>;
  }

  if (!data.items.length) {
    return (
      <Alert severity="info" className="results-widget__alert">
        Aucun résultat disponible pour cette sélection.
      </Alert>
    );
  }

  return (
    <Box className="results-section">
      <Stack className="results-section__groups" spacing={2}>
        {data.items.map((group, groupIndex) => (
          <Box key={group.date || groupIndex} className="results-section__group">
            <Typography variant="body2" className="results-section__date">
              {formatDateLabel(group.date)}
            </Typography>

            <Stack className="results-section__matches" spacing={0}>
              {(group.matches || []).map((match, matchIndex) => {
                const homeUrl = buildTeamUrl(match.homeTeam);
                const awayUrl = buildTeamUrl(match.awayTeam);
                const { homeWon, awayWon } = getWinnerFlags(match);

                return (
                  <Card
                    key={match._id || `${groupIndex}-${matchIndex}`}
                    variant="outlined"
                    className="results-match"
                  >
                    <CardContent className="results-match__content">
                      <Stack className="results-match__stack" spacing={0.75}>
                        <Box className="results-match__body">
                          <Box className="results-match__team-side results-match__team-side--home">
                            <Avatar
                              src={getTeamPhoto(match.homeTeam)}
                              alt={match.homeTeam?.name || 'home'}
                              className="results-match__logo results-match__logo--home"
                            >
                              {discipline === 'soccer' ? (
                                <SportsSoccerIcon fontSize="small" />
                              ) : (
                                <SportsRugbyIcon fontSize="small" />
                              )}
                            </Avatar>

                            <Typography
                              className={`results-match__team-name results-match__team-name--home${
                                homeWon ? ' results-match__team-name--winner' : ''
                              }`}
                            >
                              {homeUrl ? (
                                <Link
                                  href={homeUrl}
                                  underline="hover"
                                  color="inherit"
                                  onClick={(e) => handleLinkClick(e, homeUrl)}
                                >
                                  {match.homeTeam?.name || '-'}
                                </Link>
                              ) : (
                                match.homeTeam?.name || '-'
                              )}
                            </Typography>
                          </Box>

                          <Box className="results-match__center">
                            {renderCenterContent(match)}
                          </Box>

                          <Box className="results-match__team-side results-match__team-side--away">
                            <Typography
                              className={`results-match__team-name results-match__team-name--away${
                                awayWon ? ' results-match__team-name--winner' : ''
                              }`}
                            >
                              {awayUrl ? (
                                <Link
                                  href={awayUrl}
                                  underline="hover"
                                  color="inherit"
                                  onClick={(e) => handleLinkClick(e, awayUrl)}
                                >
                                  {match.awayTeam?.name || '-'}
                                </Link>
                              ) : (
                                match.awayTeam?.name || '-'
                              )}
                            </Typography>

                            <Avatar
                              src={getTeamPhoto(match.awayTeam)}
                              alt={match.awayTeam?.name || 'away'}
                              className="results-match__logo results-match__logo--away"
                            >
                              {discipline === 'soccer' ? (
                                <SportsSoccerIcon fontSize="small" />
                              ) : (
                                <SportsRugbyIcon fontSize="small" />
                              )}
                            </Avatar>
                          </Box>
                        </Box>

                        {match.matchUrl ? (
                          <Box className="results-match__live-wrap">
                            <Link
                              href={match.matchUrl}
                              underline="hover"
                              color="inherit"
                              onClick={(e) => handleLinkClick(e, match.matchUrl)}
                              className="results-match__live-link"
                            >
                              Suivez le match en live !
                            </Link>
                          </Box>
                        ) : null}
                      </Stack>
                    </CardContent>
                  </Card>
                );
              })}
            </Stack>
          </Box>
        ))}
      </Stack>
    </Box>
  );
}
